import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SessionExpiryService } from '../services/session-expiry.service';
import { LocalStorageUtils } from '../utils/user.utils';
import { ROUTES } from '../constants/app.constants';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const sessionExpiryService = inject(SessionExpiryService);

  const isAuthenticated = await authService.isUserAuthenticated();

  if (!isAuthenticated) {
    console.log('Usuario no autenticado, redirigiendo al login');
    authService.redirectToLogin();
    return false;
  }

  // El token de Supabase puede seguir siendo válido (autoRefreshToken) aunque
  // ya se haya superado el idle timeout (3h) o el tope absoluto (8h) de la
  // sesión. Este chequeo síncrono evita que una navegación pase igual mientras
  // el intervalo periódico de SessionExpiryService todavía no lo detectó.
  if (!sessionExpiryService.isSessionWithinLimits()) {
    console.log('Sesión fuera de los límites de idle/absoluto, cerrando sesión');
    await sessionExpiryService.handleSessionExpired();
    return false;
  }

  return true;
};

export const authChildGuard: CanActivateChildFn = (childRoute, state) => {
  return authGuard(childRoute, state);
};

export const loginRedirectGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const isAuthenticated = await authService.isUserAuthenticated();
  if (!isAuthenticated) {
    return true;
  }

  // OJO: este guard solo debe alejar a un usuario YA autenticado de la pantalla de
  // login (p.ej. al volver con "atrás" del navegador). Antes llamaba a
  // updateAuthenticationState(), que si currentUser aún no estaba en memoria
  // (típico al volver atrás justo después de recargar) re-consultaba el perfil por
  // RPC y, ante CUALQUIER error transitorio de esa llamada, cerraba la sesión por
  // completo (logout real, no solo un redirect). Un guard de "ya hay sesión, mándalo
  // a home" nunca debería poder terminar en un logout. Si currentUser no está en
  // memoria, usamos el fallback de localStorage sin re-verificar nada: la carga real
  // del perfil la hace el propio dashboard al montarse.
  const currentUser = authService.getCurrentUser()
    || LocalStorageUtils.getUserData(true)
    || LocalStorageUtils.getUserData(false);

  if (currentUser?.user_type_id === 1) {
    return router.parseUrl(ROUTES.DASHBOARD.USER);
  }

  return router.parseUrl(ROUTES.DASHBOARD.COMPANY);
};
